import { useParams } from 'react-router-dom'
import Masonry from 'react-masonry-css'
import PageLayout from '../components/layout/PageLayout'
import FloatingFabs from '@astro/floating-fabs'
import GalleryHeroSection from '../components/sections/gallery/GalleryHeroSection'
import ProductsCtaSection from '../components/sections/faq/ProductsCtaSection'
import { useGalleryItem } from '../hooks/useGallery'

const BREAKPOINTS = { default: 3, 1024: 3, 768: 2, 640: 1 }

function MediaCardSkeleton({ tall }) {
  return (
    <div className={`mb-4 rounded-2xl border border-[#C8922A]/10 bg-[#1C140A]/5 animate-pulse ${tall ? 'h-96' : 'h-64'}`} />
  )
}

function MediaCard({ media }) {
  if (media.type === 'video' && media.kind === 'embed') {
    return (
      <div className="mb-4 rounded-2xl overflow-hidden border border-[#C8922A]/15 bg-[#1C140A] shadow-sm aspect-video">
        <iframe
          className="w-full h-full"
          src={media.src}
          title="Gallery video"
          frameBorder="0"
          allow="autoplay; encrypted-media"
          allowFullScreen
        />
      </div>
    )
  }

  if (media.type === 'video' && media.kind === 'file') {
    return (
      <div className="mb-4 rounded-2xl overflow-hidden border border-[#C8922A]/15 bg-[#1C140A] shadow-sm">
        <video className="w-full h-auto" src={media.src} controls />
      </div>
    )
  }

  return (
    <div className="group mb-4 rounded-2xl overflow-hidden border border-[#C8922A]/15 bg-[#1C140A] shadow-sm transition-all duration-500 hover:border-[#C8922A]/50 hover:shadow-2xl hover:shadow-[#C8922A]/20">
      <img
        src={media.src}
        alt=""
        className="block w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
        loading="lazy"
      />
    </div>
  )
}

export default function GalleryAlbumPage() {
  const { id } = useParams()
  const { item: album, isPending, isError } = useGalleryItem(id)

  return (
    <PageLayout className="bg-[#403c3d]/5 text-[#151b21] antialiased min-h-screen flex flex-col">
      <main className="grow">
        <GalleryHeroSection
          breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Gallery', to: '/gallery' }, { label: album?.title ?? 'Album' }]}
          eyebrow={album ? `${album.totalImages ?? 0} PHOTOS · ${album.totalVideos ?? 0} VIDEOS` : 'Sacred Visuals'}
          title={album?.title ?? 'Loading…'}
          subtitle={album?.description ?? 'Teachings, discourses and spiritual insights from Maharaj Ji — captured for every seeker.'}
        />
        <section className="py-16 md:py-24 md:px-18" style={{ background: '#FDFAF6' }}>
          <div className="container px-6 mx-auto">
            {isError && (
              <div className="text-center py-16">
                <p className="text-[#8B7355] normal-case">Couldn't load this album.</p>
              </div>
            )}

            {!isError && isPending && (
              <Masonry breakpointCols={BREAKPOINTS} className="flex w-auto -ml-4" columnClassName="pl-4 bg-clip-padding">
                {Array.from({ length: 9 }).map((_, i) => (
                  <MediaCardSkeleton key={i} tall={i % 3 === 1} />
                ))}
              </Masonry>
            )}

            {!isError && !isPending && !album && (
              <p className="text-center text-[#8B7355] py-16 normal-case">Album not found.</p>
            )}

            {!isError && !isPending && album && (
              <>
                {album.media.length === 0 && (
                  <p className="text-center text-[#8B7355] py-16 normal-case">No media in this album yet.</p>
                )}

                {album.media.length > 0 && (
                  <Masonry breakpointCols={BREAKPOINTS} className="flex w-auto -ml-4" columnClassName="pl-4 bg-clip-padding">
                    {album.media.map((media, i) => (
                      <MediaCard key={media.src ?? i} media={media} />
                    ))}
                  </Masonry>
                )}
              </>
            )}
          </div>
        </section>
        <ProductsCtaSection />
      </main>
      <FloatingFabs />
    </PageLayout>
  )
}
